import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const LENCO_PAYMENT_URL = (
    process.env.LENCO_API_URL ||
    process.env.REACT_APP_LENCO_API_URL ||
    'https://payment-lenco-api.onrender.com/api'
).replace(/\/$/, '');
const PENDING_VOTE_PAYMENT_KEY = 'pendingVotePayment';

const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const generateReference = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 10000);
    return `vote-purchase-${timestamp}-${randomNum}`;
};

const extractVoteBalance = (payload) => {
    const source = payload?.data ?? payload ?? {};

    const direct =
        source.votes ??
        source.totalVotesRemaining ??
        source.totalVotes ??
        source.voteBalance ??
        source.votesRemaining ??
        source.availableVotes;

    if (typeof direct === 'number') {
        return { votes: direct };
    }

    if (typeof direct === 'string' && direct.trim() !== '' && !Number.isNaN(Number(direct))) {
        return { votes: Number(direct) };
    }

    // Fallback: if API returns purchase entries, sum remaining votes.
    if (Array.isArray(source)) {
        const total = source.reduce((sum, entry) => {
            const value = Number(entry?.votesRemaining ?? entry?.votes ?? 0);
            return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
        return { votes: total };
    }

    return { votes: 0 };
};

export const votePurchaseService = {
    /**
     * Get all vote packages available for purchase
     */
    getVotePackages: async () => {
        try {
            const response = await axios.get(`${API_URL}/vote-packages`, {
                headers: getAuthHeaders(),
            });
            // Filter only active packages
            const packages = Array.isArray(response.data) ? response.data : response.data.data || [];
            return packages.filter(pkg => pkg.isActive !== false);
        } catch (error) {
            throw error;
        }
    },

    /**
     * Initiate vote purchase payment
     * Creates a transaction record and starts payment process
     */
    initiateVotePurchase: async (packageId, paymentDetails) => {
        try {
            const userId = localStorage.getItem('userId');
            const referenceId = generateReference();

            // 1. Create purchase record in database
            const purchaseResponse = await axios.post(
                `${API_URL}/vote-purchases`,
                {
                    userId,
                    packageId,
                    referenceId,
                    amount: paymentDetails.amount,
                    status: 'pending',
                },
                {
                    headers: getAuthHeaders(),
                }
            );

            // 2. Initiate payment with Lenco
            const paymentResponse = await axios.post(
                `${LENCO_PAYMENT_URL}/payment/request`,
                {
                    amount: paymentDetails.amount,
                    reference: referenceId,
                    phone: paymentDetails.phone,
                    operator: paymentDetails.operator, // 'mtn', 'airtel', 'zamtel'
                    country: 'zm',
                    bearer: 'customer'
                }
            );

            return {
                success: true,
                referenceId,
                purchaseId: purchaseResponse.data.data?.purchaseId || purchaseResponse.data.data?.id,
                paymentResponse: paymentResponse.data,
            };
        } catch (error) {
            throw error;
        }
    },

    /**
     * Store pending vote payment so verification can resume after reload.
     */
    setPendingVotePayment: (payload) => {
        localStorage.setItem(PENDING_VOTE_PAYMENT_KEY, JSON.stringify(payload));
    },

    /**
     * Get pending vote payment details from storage.
     */
    getPendingVotePayment: () => {
        const raw = localStorage.getItem(PENDING_VOTE_PAYMENT_KEY);
        if (!raw) return null;

        try {
            return JSON.parse(raw);
        } catch {
            localStorage.removeItem(PENDING_VOTE_PAYMENT_KEY);
            return null;
        }
    },

    /**
     * Clear pending vote payment reference from storage.
     */
    clearPendingVotePayment: () => {
        localStorage.removeItem(PENDING_VOTE_PAYMENT_KEY);
    },

    /**
     * Update pending payment structure for backward compatibility.
     * Handles old pending payments that might not have all fields.
     */
    updatePendingVotePayment: (updates) => {
        const existing = votePurchaseService.getPendingVotePayment();
        if (existing) {
            const updated = { ...existing, ...updates };
            votePurchaseService.setPendingVotePayment(updated);
            return updated;
        }
        return null;
    },

    /**
     * Verify payment status for a vote purchase
     */
    verifyVotePayment: async (referenceId) => {
        try {
            const response = await axios.get(
                `${LENCO_PAYMENT_URL}/payment/verify/${referenceId}`
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Verify and complete a pending vote payment saved in localStorage.
     * Returns status: pending | successful | failed | none
     * 
     * IMPORTANT: The backend verifies payment with Lenco before crediting votes.
     * This prevents votes from being added if payment failed.
     */
    verifyPendingVotePayment: async () => {
        const pending = votePurchaseService.getPendingVotePayment();
        if (!pending?.referenceId) {
            return { status: 'none' };
        }

        try {
            const verifyResponse = await votePurchaseService.verifyVotePayment(pending.referenceId);
            const paymentStatus = verifyResponse?.data?.status;

            if (verifyResponse?.status === true && paymentStatus === 'successful') {
                try {
                    await votePurchaseService.completeVotePurchase(
                        pending.referenceId,
                        {
                            purchaseId: pending?.purchaseId,
                            paymentConfirmed: true,
                            ...(pending.packageData || {}),
                            packageId: pending?.packageData?.packageId || pending?.packageId,
                        }
                    );
                    votePurchaseService.clearPendingVotePayment();
                    return {
                        status: 'successful',
                        referenceId: pending.referenceId,
                        verifyResponse,
                    };
                } catch (completeError) {
                    console.error('Backend rejected completion:', completeError?.response?.data?.message);
                    // Backend verification failed - don't add votes
                    return {
                        status: 'failed',
                        referenceId: pending.referenceId,
                        message: completeError?.response?.data?.message || 'Backend verification failed'
                    };
                }
            }

            if (paymentStatus === 'failed' || paymentStatus === 'cancelled') {
                votePurchaseService.clearPendingVotePayment();
                return {
                    status: 'failed',
                    referenceId: pending.referenceId,
                    verifyResponse,
                };
            }

            return {
                status: 'pending',
                referenceId: pending.referenceId,
                verifyResponse,
            };
        } catch (error) {
            console.error('Error during pending payment verification:', error);
            return {
                status: 'pending',
                referenceId: pending.referenceId,
                error: error.message
            };
        }
    },

    /**
     * Complete vote purchase after successful payment
     */
    completeVotePurchase: async (referenceId, packageData) => {
        try {
            const userId = packageData?.userId || localStorage.getItem('userId');
            const response = await axios.post(
                `${API_URL}/vote-purchases/complete`,
                {
                    referenceId,
                    userId,
                    ...packageData,
                },
                {
                    headers: getAuthHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Get user's vote balance
     */
    getUserVoteBalance: async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(
                `${API_URL}/users/${userId}/vote-balance`,
                {
                    headers: getAuthHeaders(),
                }
            );
            return extractVoteBalance(response.data);
        } catch (error) {
            throw error;
        }
    },

    /**
     * Get user's purchase history
     */
    getPurchaseHistory: async (limit = 10) => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(
                `${API_URL}/users/${userId}/vote-purchases?limit=${limit}`,
                {
                    headers: getAuthHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};
